import React from "react";
import {
  Grid,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Button,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LanguageSwitcher from "../../../utilities/LanguageSwitcher";
import PasswordValidator from "password-validator";
import parsePhoneNumber from "libphonenumber-js";
import AuthContext from "../../../contexts/AuthContext";
import { signup, validateEmail } from "../../../utilities/authFlow";
import { buildConfirmMetadata } from "../../../utilities/walkup";

// List of all international phone country codes
const COUNTRY_CODES = [
  "+1",
  "+7",
  "+20",
  "+27",
  "+30",
  "+31",
  "+32",
  "+33",
  "+34",
  "+36",
  "+39",
  "+40",
  "+41",
  "+43",
  "+44",
  "+45",
  "+46",
  "+47",
  "+48",
  "+49",
  "+51",
  "+52",
  "+53",
  "+54",
  "+55",
  "+56",
  "+57",
  "+58",
  "+60",
  "+61",
  "+62",
  "+63",
  "+64",
  "+65",
  "+66",
  "+81",
  "+82",
  "+84",
  "+86",
  "+90",
  "+91",
  "+92",
  "+93",
  "+94",
  "+95",
  "+98",
  "+211",
  "+212",
  "+213",
  "+216",
  "+218",
  "+220",
  "+221",
  "+222",
  "+223",
  "+224",
  "+225",
  "+226",
  "+227",
  "+228",
  "+229",
  "+230",
  "+231",
  "+232",
  "+233",
  "+234",
  "+235",
  "+236",
  "+237",
  "+238",
  "+239",
  "+240",
  "+241",
  "+242",
  "+243",
  "+244",
  "+245",
  "+246",
  "+248",
  "+249",
  "+250",
  "+251",
  "+252",
  "+253",
  "+254",
  "+255",
  "+256",
  "+257",
  "+258",
  "+260",
  "+261",
  "+262",
  "+263",
  "+264",
  "+265",
  "+266",
  "+267",
  "+268",
  "+269",
  "+350",
  "+351",
  "+352",
  "+353",
  "+354",
  "+355",
  "+356",
  "+357",
  "+358",
  "+359",
  "+370",
  "+371",
  "+372",
  "+373",
  "+374",
  "+375",
  "+376",
  "+377",
  "+378",
  "+379",
  "+380",
  "+381",
  "+382",
  "+383",
  "+385",
  "+386",
  "+387",
  "+389",
  "+420",
  "+421",
  "+423",
  "+500",
  "+501",
  "+502",
  "+503",
  "+504",
  "+505",
  "+506",
  "+507",
  "+508",
  "+509",
  "+590",
  "+591",
  "+592",
  "+593",
  "+594",
  "+595",
  "+596",
  "+597",
  "+598",
  "+599",
  "+670",
  "+672",
  "+673",
  "+674",
  "+675",
  "+676",
  "+677",
  "+678",
  "+679",
  "+680",
  "+681",
  "+682",
  "+683",
  "+685",
  "+686",
  "+687",
  "+688",
  "+689",
  "+690",
  "+691",
  "+692",
  "+850",
  "+852",
  "+853",
  "+855",
  "+856",
  "+870",
  "+880",
  "+886",
  "+960",
  "+961",
  "+962",
  "+963",
  "+964",
  "+965",
  "+966",
  "+967",
  "+968",
  "+970",
  "+971",
  "+972",
  "+973",
  "+974",
  "+975",
  "+976",
  "+977",
  "+992",
  "+993",
  "+994",
  "+995",
  "+996",
  "+998",
];

const Signup = ({ setPage, setPw, config }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { email, setEmail, appStyle, orgId, appConfig } =
    React.useContext(AuthContext);
  // const [email, setEmail] = React.useState("");
  const [passwordVisibility, setPasswordVisibility] = React.useState(false);
  const [rightIcon, setRightIcon] = React.useState("eye");
  const [error, setError] = React.useState("");
  const [appStyleError, setAppStyleError] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [password2, setPassword2] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [countryCode, setCountryCode] = React.useState("+1");
  const [emailError, setEmailError] = React.useState(null);
  const [passwordError, setPasswordError] = React.useState(null);
  const [passwordConfirmError, setPasswordConfirmError] = React.useState(null);
  const [phoneError, setPhoneError] = React.useState(null);
  const [checked, setChecked] = React.useState(false);
  const [smsChecked, setSmsChecked] = React.useState(true);
  const [emailSuggestion, setEmailSuggestion] = React.useState(null);

  React.useEffect(() => {
    if (config?.defaultCountryCode) {
      setCountryCode(config.defaultCountryCode);
    }
  }, [config]);

  React.useEffect(() => {
    const hasAppStyle = appStyle && Object.keys(appStyle).length > 0;

    if (!hasAppStyle) {
      setAppStyleError("We’re having trouble connecting. Please refresh the page. VPNs or work devices may block access.");
    } else {
      setAppStyleError("");
    }
  }, [appStyle]);

  const handleEmailValidation = async () => {
    console.log("validating email");

    const result = await validateEmail(email);
    setEmailSuggestion(result.suggestion || null);
    if (!result.success) {
      setError(result.error);
    } else {
      setError("");
    }
  };

  const applySuggestion = () => {
    if (!emailSuggestion) return;
    setEmail(emailSuggestion);
    setEmailSuggestion(null);
    setError("");
  };

  const validatePassword = () => {
    console.log("validating password");
    const schema = new PasswordValidator();
    schema
      .is()
      .min(8, "Password is too short - min 8")
      .has()
      .uppercase(1, "At least 1 uppercase letter required")
      .has()
      .lowercase(1, "At least 1 lowercase letter required")
      .has()
      .digits(1, "At least 1 number required")
      .has()
      .symbols(1, "At least 1 symbol required");

    const valid = schema.validate(password, { details: true });
    console.log(`password valid: ${valid}`);
    if (valid.length > 0) {
      setPasswordError(valid[0].message);
      return;
    }
    if (valid.length === 0 && passwordError) {
      setPasswordError(null);
    }
  };

  const validatePasswordConfirm = () => {
    console.log("validating password confirm");
    if (password !== password2) {
      setPasswordConfirmError(true);
    } else {
      setPasswordConfirmError(null);
    }
  };

  const validatePhone = () => {
    console.log("validating phone");
    if (!phone) {
      setPhoneError(true);
      return false;
    }
    const phoneNumber = parsePhoneNumber(`${countryCode}${phone}`);
    if (!phoneNumber || !phoneNumber.isValid()) {
      console.log("Invalid phone");
      setPhoneError(true);
      return false;
    }
    if (phoneNumber.isValid() && phoneError) {
      setPhoneError(null);
    }
    return true;
  };

  const handleCheck = () => {
    setChecked(!checked);
    if (error && !checked === true) {
      setError("");
    }
  };

  const handleSignup = async () => {
    if (appStyleError) {
      return;
    }
    if (!validatePhone()) {
      return;
    }
    if (emailError || passwordError || passwordConfirmError) {
      return;
    }

    if (!checked) {
      setError("Please confirm you agree to our Privacy Policy");
      return;
    }

    const phoneNumber = parsePhoneNumber(`${countryCode}${phone}`);

    try {
      const result = await signup(
        email,
        password,
        phoneNumber.number,
        orgId,
        smsChecked,
        buildConfirmMetadata(),
      );
      if (result.success) {
        console.log("signup success");
        setPw(password);
        setPage("confirm");
      } else {
        setError(result.error);
      }
    } catch (e) {
      var error = `Unknown error: ${e.message}`;
      console.log(error);
      setError(error);

      return { success: false, error };
    }
  };

  return (
    <Grid container spacing={3} justifyContent="center">
      <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
        <img src={appStyle?.logo} height="175px" />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        lg={6}
        style={{ display: "flex", justifyContent: "center" }}
      >
        <form
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <Paper elevation={3} style={{ width: "100%", padding: 10 }}>
            <Grid container direction="column" spacing={3}>
              <Grid item style={{ marginTop: 20, marginBottom: 20 }}>
                <Typography variant="h5" align="center">
                  {t("auth.signup.title")}
                </Typography>
              </Grid>
              {appStyleError ? (
                <Grid item>
                  <Alert severity="error">{appStyleError}</Alert>
                </Grid>
              ) : error ? (
                <Grid item>
                  <Alert severity="error">{error}</Alert>
                </Grid>
              ) : (
                <></>
              )}
              <Grid item style={{ display: "flex", justifyContent: "center" }}>
                <TextField
                  autoFocus
                  label={t("email.textbox.label")}
                  variant="outlined"
                  value={email}
                  onChange={(e) => setEmail(e.target.value.toLowerCase())}
                  placeholder={t("email.textbox.placeholder")}
                  style={{ width: "90%" }}
                  sx={{
                    fieldset: { borderColor: "red" },
                  }}
                  inputProps={{ autoCapitalize: "none" }}
                  autoComplete="off"
                  onBlur={(e) => {
                    const trimmedEmail = e.target.value.trim();
                    setEmail(trimmedEmail);
                    handleEmailValidation();
                  }}
                  error={emailError ? true : false}
                  helperText={emailError ? t("email.textbox.error") : ""}
                />
              </Grid>
              {emailSuggestion && (
                <Grid
                  item
                  style={{ display: "flex", justifyContent: "center", marginTop: -16 }}
                >
                  <Typography variant="subtitle2" align="center">
                    Did you mean{" "}
                    <span
                      onClick={applySuggestion}
                      style={{
                        color: theme.palette.primary.main,
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      {emailSuggestion}
                    </span>
                    ?
                  </Typography>
                </Grid>
              )}
              <Grid item style={{ display: "flex", justifyContent: "center" }}>
                <TextField
                  margin="dense"
                  label={t("password.textbox.label")}
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ width: "90%" }}
                  type={passwordVisibility ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        onClick={() =>
                          setPasswordVisibility(!passwordVisibility)
                        }
                      >
                        {passwordVisibility ? (
                          <VisibilityOff style={{ cursor: "pointer" }} />
                        ) : (
                          <Visibility style={{ cursor: "pointer" }} />
                        )}
                      </InputAdornment>
                    ),
                  }}
                  autoComplete="off"
                  onBlur={validatePassword}
                  error={passwordError ? true : false}
                  helperText={passwordError ? passwordError : ""}
                />
              </Grid>
              <Grid item style={{ display: "flex", justifyContent: "center" }}>
                <TextField
                  margin="dense"
                  label={t("password.confirm.label")}
                  variant="outlined"
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  style={{ width: "90%" }}
                  type={passwordVisibility ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        onClick={() =>
                          setPasswordVisibility(!passwordVisibility)
                        }
                      >
                        {passwordVisibility ? (
                          <VisibilityOff style={{ cursor: "pointer" }} />
                        ) : (
                          <Visibility style={{ cursor: "pointer" }} />
                        )}
                      </InputAdornment>
                    ),
                  }}
                  autoComplete="off"
                  onBlur={validatePasswordConfirm}
                  error={passwordConfirmError ? true : false}
                  helperText={
                    passwordConfirmError ? t("password.confirm.error") : ""
                  }
                />
              </Grid>
              <Grid item style={{ display: "flex", justifyContent: "center" }}>
                <Grid container spacing={1} style={{ width: "90%" }}>
                  <Grid item xs={3}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel id="country-code-label">Code</InputLabel>
                      <Select
                        labelId="country-code-label"
                        label="Code"
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                      >
                        {COUNTRY_CODES.map((code) => (
                          <MenuItem key={code} value={code}>
                            {code}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={9}>
                    <TextField
                      label={t("phone.textbox.label")}
                      variant="outlined"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder={t("phone.textbox.placeholder")}
                      fullWidth
                      autoComplete="off"
                      onBlur={validatePhone}
                      error={phoneError ? true : false}
                      inputProps={{ inputMode: "numeric" }}
                      helperText={phoneError ? t("phone.textbox.error") : ""}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <FormGroup sx={{ paddingLeft: "18px" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={() => handleCheck()}
                      />
                    }
                    label={
                      <Typography variant="subtitle2">
                        {t("auth.privacy.checkbox.prefix")}{" "}
                        <a
                          href={appConfig?.privacyPolicy}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: theme.palette.primary.main,
                            textDecoration: "underline",
                          }}
                        >
                          {t("privacypolicy.link")}
                        </a>
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={smsChecked}
                        onChange={() => setSmsChecked(!smsChecked)}
                      />
                    }
                    label={
                      <Typography variant="subtitle2">
                        {t("auth.sms.checkbox.prefix")} {appConfig?.orgName}{" "}
                        {t("auth.sms.checkbox.description")}
                      </Typography>
                    }
                  />
                </FormGroup>
              </Grid>

              <Grid item style={{ display: "flex", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  style={{
                    width: "90%",
                    height: 40,
                    backgroundColor: appStyleError || error
                      ? "grey"
                      : theme.palette.primary.main,
                  }}
                  disabled={appStyleError || error ? true : false}
                  onClick={handleSignup}
                >
                  {t("auth.createaccount.btn")}
                </Button>
              </Grid>
              <Grid item style={{ display: "flex", justifyContent: "center" }}>
                <Button
                  style={{
                    width: "90%",
                    height: 40,
                    color: theme.palette.primary.main,
                  }}
                  onClick={() => setPage("login")}
                >
                  {t("auth.backtologin.btn")}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </form>
      </Grid>
      <Grid item xs={12}>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Typography align="center" style={{ color: "#353535" }}>
              &copy; Aisle 2 LLC All Rights Reserved
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              align="center"
              style={{ color: "#353535", cursor: "pointer" }}
              onClick={() => window.open(appConfig?.privacyPolicy, "_blank")}
            >
              {t("privacypolicy.link")}
            </Typography>
          </Grid>
          <Grid item style={{ marginBottom: 50 }}>
            <LanguageSwitcher />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Signup;
